import { useEffect, useState } from "react";
import { KeyRound, Package, RefreshCw, Save, ShieldCheck } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { Reveal, SectionTag, money } from "../components/bits";
import SEO from "../components/SEO";

const INPUT =
  "w-full rounded-xl border border-[#39FF14]/20 bg-[#0A150F] px-4 py-3 text-sm text-[#F0FDF4] placeholder-[#4ADE80]/40 outline-none transition-colors focus:border-[#39FF14]/70";

const STOCK_OPTIONS = [
  { value: "in", label: "In stock" },
  { value: "back_soon", label: "Back soon" },
  { value: "sold_out", label: "Sold out" },
];

const STATUS_STYLES = {
  confirmed: "border-[#39FF14]/40 bg-[#39FF14]/10 text-[#39FF14]",
  pending_payment: "border-amber-400/40 bg-amber-400/10 text-amber-300",
  failed: "border-red-400/40 bg-red-400/10 text-red-300",
};
const STATUS_LABELS = { confirmed: "Paid", pending_payment: "Awaiting payment", failed: "Failed" };
const METHOD_LABELS = { card: "Card · Stripe", paypal: "PayPal" };

function OrderCard({ o }) {
  const itemsLine = o.items.map((i) => `${i.qty}× ${i.name} (${i.variant_name})`).join(", ");
  return (
    <div className="glass rounded-2xl p-5" data-testid={`admin-order-${o.order_id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="font-display font-bold text-[#F0FDF4]">{o.order_id}</span>
          <span className={`rounded-full border px-2.5 py-0.5 font-mono2 text-[10px] uppercase tracking-widest ${STATUS_STYLES[o.status] || STATUS_STYLES.pending_payment}`}>
            {STATUS_LABELS[o.status] || o.status}
          </span>
        </div>
        <div className="font-display text-lg font-bold text-[#F0FDF4]">{money(o.total)}</div>
      </div>
      <div className="mt-3 grid gap-1.5 text-xs text-[#9FB8A8] sm:grid-cols-2">
        <div>{o.customer.name} · {o.customer.email}</div>
        <div>{o.customer.city}, {o.customer.state} {o.customer.postcode}</div>
        <div className="sm:col-span-2">{itemsLine}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono2 text-[10px] uppercase tracking-widest text-[#4ADE80]/70">
          <span>{METHOD_LABELS[o.payment_method] || o.payment_method}</span>
          <span>{new Date(o.created_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>
          <span>{o.tracking_code}</span>
        </div>
      </div>
    </div>
  );
}

export default function Admin() {
  const [key, setKey] = useState(() => localStorage.getItem("stl-admin-key") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("prices");
  const [products, setProducts] = useState([]);
  const [prices, setPrices] = useState({});
  const [stocks, setStocks] = useState({});
  const [orig, setOrig] = useState({ prices: {}, stocks: {} });
  const [saving, setSaving] = useState(false);
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const load = () =>
    api.get("/products").then((r) => {
      setProducts(r.data);
      const p0 = {};
      const s0 = {};
      r.data.forEach((p) =>
        p.variants.forEach((v) => {
          p0[`${p.slug}__${v.sku}`] = String(v.price);
          s0[`${p.slug}__${v.sku}`] = v.stock || "in";
        })
      );
      setPrices(p0);
      setStocks(s0);
      setOrig({ prices: p0, stocks: s0 });
    });

  const loadOrders = () => {
    setOrdersLoading(true);
    api.get("/admin/orders", { headers: { "x-admin-key": key } })
      .then((r) => setOrders(r.data))
      .catch(() => toast.error("Couldn't load orders"))
      .finally(() => setOrdersLoading(false));
  };

  useEffect(() => {
    load();
    document.title = "Store Control — SuperTechLED";
  }, []);

  useEffect(() => {
    if (authed && tab === "orders") loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab]);

  const unlock = async () => {
    try {
      await api.post("/admin/prices", { updates: [] }, { headers: { "x-admin-key": key } });
      localStorage.setItem("stl-admin-key", key);
      setAuthed(true);
      toast.success("Welcome back, boss");
    } catch {
      toast.error("Wrong admin key");
    }
  };

  const dirtyKeys = Object.keys(prices).filter((k) => prices[k] !== orig.prices[k] || stocks[k] !== orig.stocks[k]);

  const save = async () => {
    if (!dirtyKeys.length || saving) return;
    setSaving(true);
    try {
      const updates = dirtyKeys.map((k) => {
        const [slug, sku] = k.split("__");
        return { slug, sku, price: parseFloat(prices[k]), stock: stocks[k] };
      });
      const { data } = await api.post("/admin/prices", { updates }, { headers: { "x-admin-key": key } });
      toast.success(`${data.updated} item${data.updated === 1 ? "" : "s"} updated — store & Stripe synced`);
      await load();
    } catch (e) {
      if (e.response?.status === 401) {
        setAuthed(false);
        toast.error("Admin key rejected");
      } else {
        toast.error("Save failed — check the values and try again");
      }
    } finally {
      setSaving(false);
    }
  };

  const paidOrders = orders.filter((o) => o.status === "confirmed");
  const awaiting = orders.filter((o) => o.status === "pending_payment");
  const revenue = paidOrders.reduce((s, o) => s + o.total, 0);

  const tabBtn = (id, label) => (
    <button
      data-testid={`admin-tab-${id}`}
      onClick={() => setTab(id)}
      className={`rounded-full px-6 py-2.5 font-mono2 text-[11px] uppercase tracking-[0.2em] transition-all ${
        tab === id ? "bg-[#39FF14] font-bold text-[#050B07]" : "border border-[#39FF14]/25 text-[#86EFAC] hover:border-[#39FF14]/60"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="mx-auto max-w-4xl px-5 pb-28 pt-28 sm:px-8" data-testid="admin-page">
      <SEO title="Store Control — SuperTechLED" noindex />
      <Reveal>
        <SectionTag>Store control</SectionTag>
        <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-[#F0FDF4] sm:text-4xl">
          {tab === "prices" ? "Prices & stock" : "Orders"}
        </h1>
      </Reveal>

      {!authed ? (
        <Reveal delay={0.1}>
          <div className="glass mx-auto mt-14 max-w-md rounded-2xl p-8 text-center">
            <KeyRound className="mx-auto h-8 w-8 text-[#39FF14]" />
            <h2 className="font-display mt-4 text-xl font-bold text-[#F0FDF4]">Admin key required</h2>
            <input
              data-testid="admin-key-input"
              type="password"
              className={`${INPUT} mt-5 text-center`}
              placeholder="Enter admin key"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && unlock()}
            />
            <button
              data-testid="admin-unlock-button"
              onClick={unlock}
              className="mt-4 w-full rounded-full bg-[#39FF14] py-3 font-display text-sm font-bold text-[#050B07] transition-shadow hover:shadow-[0_0_30px_rgba(57,255,20,0.4)]"
            >
              Unlock dashboard
            </button>
          </div>
        </Reveal>
      ) : (
        <Reveal delay={0.1}>
          <div className="mt-8 flex items-center justify-between gap-4">
            <div className="flex gap-2">{tabBtn("prices", "Prices & stock")}{tabBtn("orders", "Orders")}</div>
            {tab === "orders" && (
              <button data-testid="admin-orders-refresh" onClick={loadOrders} className="flex items-center gap-2 rounded-full border border-[#39FF14]/30 px-4 py-2 font-mono2 text-[10px] uppercase tracking-widest text-[#86EFAC] hover:border-[#39FF14]/60">
                <RefreshCw className={`h-3.5 w-3.5 ${ordersLoading ? "animate-spin" : ""}`} /> Refresh
              </button>
            )}
          </div>

          {tab === "prices" ? (
            <>
              <div className="mt-6 flex items-center gap-2 rounded-xl border border-[#39FF14]/20 bg-[#0E1C13] px-4 py-2.5 text-xs text-[#86EFAC]">
                <ShieldCheck className="h-4 w-4 text-[#39FF14]" />
                Editing live prices & stock — customers see changes immediately. Sold-out items can't be purchased.
              </div>
              <div className="mt-6 space-y-4">
                {products.map((p) => (
                  <div key={p.slug} className="glass rounded-2xl p-5" data-testid={`admin-product-${p.slug}`}>
                    <div className="flex items-center gap-3">
                      <img src={p.image} alt={p.name} className="h-10 w-10 rounded-lg bg-[#050B07] object-contain p-1" />
                      <div className="font-display font-bold text-[#F0FDF4]">{p.name}</div>
                    </div>
                    <div className="mt-4 space-y-2.5">
                      {p.variants.map((v) => {
                        const id = `${p.slug}__${v.sku}`;
                        const dirty = prices[id] !== orig.prices[id] || stocks[id] !== orig.stocks[id];
                        return (
                          <div key={v.sku} className="flex flex-wrap items-center gap-3" data-testid={`admin-row-${v.sku}`}>
                            <div className="min-w-40 flex-1 text-sm text-[#C9DCD0]">{v.name}</div>
                            <select
                              data-testid={`stock-select-${v.sku}`}
                              value={stocks[id] ?? "in"}
                              onChange={(e) => setStocks((s) => ({ ...s, [id]: e.target.value }))}
                              className={`rounded-lg border bg-[#050B07] px-3 py-2 font-mono2 text-[11px] outline-none transition-colors ${
                                stocks[id] === "sold_out"
                                  ? "border-red-400/50 text-red-300"
                                  : stocks[id] === "back_soon"
                                    ? "border-amber-400/50 text-amber-300"
                                    : "border-[#39FF14]/20 text-[#F0FDF4]"
                              }`}
                            >
                              {STOCK_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            <div className="flex items-center gap-1.5">
                              <span className="font-mono2 text-xs text-[#4ADE80]">A$</span>
                              <input
                                data-testid={`price-input-${v.sku}`}
                                type="number"
                                min="1"
                                step="1"
                                value={prices[id] ?? ""}
                                onChange={(e) => setPrices((s) => ({ ...s, [id]: e.target.value }))}
                                className={`w-28 rounded-lg border bg-[#050B07] px-3 py-2 text-right font-mono2 text-sm outline-none transition-colors ${
                                  dirty ? "border-[#39FF14] text-[#39FF14]" : "border-[#39FF14]/20 text-[#F0FDF4]"
                                }`}
                              />
                            </div>
                            {dirty && (
                              <span className="font-mono2 text-[10px] uppercase tracking-widest text-[#4ADE80]/70 line-through">
                                {money(parseFloat(orig.prices[id] || "0"))}
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <div className="sticky bottom-5 mt-8 flex gap-3">
                <button
                  data-testid="admin-save-button"
                  onClick={save}
                  disabled={!dirtyKeys.length || saving}
                  className={`flex flex-1 items-center justify-center gap-2 rounded-full py-4 font-display text-sm font-bold transition-all ${
                    dirtyKeys.length && !saving
                      ? "bg-[#39FF14] text-[#050B07] hover:shadow-[0_0_36px_rgba(57,255,20,0.45)]"
                      : "cursor-not-allowed bg-[#39FF14]/20 text-[#39FF14]/40"
                  }`}
                >
                  {saving ? (
                    <motion.span animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 0.9, ease: "linear" }} className="h-4 w-4 rounded-full border-2 border-[#050B07]/30 border-t-[#050B07]" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  {saving ? "Saving & syncing…" : dirtyKeys.length ? `Save ${dirtyKeys.length} change${dirtyKeys.length === 1 ? "" : "s"}` : "No changes"}
                </button>
                <button
                  data-testid="admin-reset-button"
                  onClick={() => { setPrices(orig.prices); setStocks(orig.stocks); }}
                  disabled={!dirtyKeys.length}
                  className="flex items-center gap-2 rounded-full border border-[#39FF14]/30 px-6 font-display text-sm font-bold text-[#F0FDF4] hover:border-[#39FF14]/60 disabled:opacity-40"
                >
                  <RefreshCw className="h-4 w-4" /> Reset
                </button>
              </div>
            </>
          ) : (
            <div className="mt-6" data-testid="admin-orders-list">
              <div className="grid grid-cols-3 gap-3">
                <div className="glass rounded-2xl p-4 text-center">
                  <div className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#4ADE80]">Orders</div>
                  <div className="font-display mt-1 text-2xl font-black text-[#F0FDF4]" data-testid="admin-orders-count">{orders.length}</div>
                </div>
                <div className="glass rounded-2xl p-4 text-center">
                  <div className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#4ADE80]">Awaiting payment</div>
                  <div className={`font-display mt-1 text-2xl font-black ${awaiting.length ? "text-amber-300" : "text-[#F0FDF4]"}`} data-testid="admin-orders-awaiting">
                    {awaiting.length}
                  </div>
                </div>
                <div className="glass rounded-2xl p-4 text-center">
                  <div className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-[#4ADE80]">Paid revenue</div>
                  <div className="font-display mt-1 text-2xl font-black text-[#39FF14]" data-testid="admin-orders-revenue">{money(revenue)}</div>
                </div>
              </div>
              {ordersLoading && orders.length === 0 ? (
                <div className="mt-10 text-center font-mono2 text-xs uppercase tracking-[0.3em] text-[#4ADE80]">Loading orders…</div>
              ) : orders.length === 0 ? (
                <div className="glass mt-6 rounded-2xl p-10 text-center">
                  <Package className="mx-auto h-8 w-8 text-[#39FF14]/40" />
                  <p className="mt-3 text-sm text-[#9FB8A8]">No orders yet — they'll land here the moment a customer checks out.</p>
                </div>
              ) : (
                <div className="mt-6 space-y-4">
                  {orders.map((o) => (
                    <OrderCard key={o.order_id} o={o} />
                  ))}
                </div>
              )}
            </div>
          )}
        </Reveal>
      )}
    </div>
  );
}
